'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CaseStudyCardProps {
  id: string;
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  image: string;
  alt: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  timeline: string;
  tags: string[];
}

const CaseStudyCard = ({ 
  id, 
  title, 
  client, 
  industry, 
  challenge, 
  solution, 
  image, 
  alt, 
  results, 
  timeline, 
  tags 
}: CaseStudyCardProps) => {
  return (
    <Link href={`/case-study-detail?id=${id}`}>
      <div className="group relative bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-500 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative h-56 overflow-hidden">
          <AppImage
            src={image}
            alt={alt}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
          <div className="absolute top-4 right-4 px-3 py-1.5 bg-primary/90 backdrop-blur-sm rounded-full text-xs font-semibold text-primary-foreground">
            {industry}
          </div>
        </div>

        <div className="relative p-6 space-y-4">
          <div>
            <h3 className="text-xl font-display font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
              {title}
            </h3>
            <p className="text-sm font-medium text-primary mb-3">{client}</p>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Uitdaging
              </p>
              <p className="text-sm text-foreground/80 line-clamp-2">
                {challenge}
              </p>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Oplossing
              </p>
              <p className="text-sm text-foreground/80 line-clamp-2">
                {solution}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
            {results.slice(0, 2).map((result, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={result.icon as any} size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-lg font-bold text-primary">{result.value}</p>
                  <p className="text-xs text-muted-foreground">{result.metric}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="ClockIcon" size={16} />
              <span>{timeline}</span>
            </div>
            <div className="flex items-center space-x-2 text-primary font-semibold group-hover:translate-x-1 transition-transform duration-300">
              <span className="text-sm">Lees meer</span>
              <Icon name="ArrowRightIcon" size={16} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted rounded-md text-xs font-medium text-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CaseStudyCard;